<template>
  <div v-tooltip="avatarTooltipAttrs" :class="avatarClass" v-bind="$attrs">
    <slot>
      <div
        v-show="isImageLoaded"
        class="sb-avatar__image"
        :style="avatarStyle"
        :data-testid="`${dataTestid}-image`"
      >
        <img
          :src="src"
          :alt="friendlyName || name"
          @load="handleImageLoad"
          @error="handleImageError"
        />
      </div>
      <span
        v-if="!isImageLoaded && (name || friendlyName)"
        :class="['sb-avatar__initials', backgroundColorClass]"
        :data-testid="`${dataTestid}-initials`"
        aria-hidden="true"
      >
        {{ initials }}
      </span>
      <svg
        v-else-if="!isImageLoaded && !name && !friendlyName"
        :class="['sb-avatar__fallback-icon', backgroundColorClass]"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g fill="none" fill-rule="evenodd">
          <circle cx="12" cy="12" r="12" />
          <path
            fill="currentColor"
            d="M10.53 14.47c.537.536 1.024.78 1.47.78.446 0 .933-.244 1.47-.78a.75.75 0 0 1 1.06 1.06c-.796.797-1.642 1.22-2.53 1.22-.888 0-1.734-.423-2.53-1.22a.75.75 0 0 1 1.06-1.06zM9 9a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm6 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
          />
          <circle cx="17.5" cy="13.5" r="2.5" opacity=".091" />
          <circle cx="6.5" cy="13.5" r="2.5" opacity=".091" />
        </g>
      </svg>
    </slot>

    <div v-if="showTextContainer" class="sb-avatar__text-container">
      <span
        v-if="isDescriptionTop"
        :class="descriptionClass"
        :data-testid="`${dataTestid}-top-description`"
      >
        {{ description }}
      </span>
      <span class="sb-avatar__text" :data-testid="`${dataTestid}-text`">
        {{ friendlyName || name }}
      </span>
      <span
        v-if="isDescriptionBottom"
        :class="descriptionClass"
        :data-testid="`${dataTestid}-bottom-description`"
      >
        {{ description }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue'
import {
  getInitials,
  generateRandomBgColor,
  type AvatarBackgroundColor,
  type AvatarSize,
} from './utils'
import { Tooltip as vTooltip } from '../../directives'

export type AvatarProps = {
  bgColor?: AvatarBackgroundColor
  description?: string
  descriptionPosition?: 'top' | 'bottom'
  name?: string
  friendlyName?: string
  size?: AvatarSize
  showName?: boolean
  src?: string
  borderColor?: string
  useTooltip?: boolean
  tooltipVariant?: string
  tooltipFullLength?: boolean
  tooltipPosition?: string
}

const props = withDefaults(defineProps<AvatarProps>(), {
  bgColor: undefined,
  description: undefined,
  descriptionPosition: 'top',
  name: undefined,
  friendlyName: undefined,
  size: undefined,
  showName: false,
  src: undefined,
  borderColor: undefined,
  useTooltip: false,
  tooltipVariant: 'dark',
  tooltipFullLength: true,
  tooltipPosition: 'bottom',
})

const attrs = useAttrs()
const isImageLoaded = ref(false)

const dataTestid = computed(() => attrs['data-testid'] || 'sb-avatar')

const avatarClass = computed(() => {
  const avatarSizeClass = props.size && props.size !== 'normal' && `sb-avatar--${props.size}`
  return ['sb-avatar', avatarSizeClass]
})

const avatarStyle = computed(() => {
  const isColorValid = props.borderColor?.startsWith('#')

  return {
    borderColor: isColorValid ? props.borderColor : undefined,
    borderWidth: isColorValid ? '2px' : undefined,
  }
})

const backgroundColorClass = computed(() => {
  const color =
    props.bgColor || generateRandomBgColor(props.friendlyName || props.name)

  return `sb-avatar--bg-color-${color}`
})

const descriptionClass = computed(() => {
  return [
    'sb-avatar__description',
    `sb-avatar__description--${props.descriptionPosition}`,
  ]
})

const avatarTooltipAttrs = computed(() => {
  return (
    props.useTooltip && {
      label: props.friendlyName ? props.friendlyName : props.name,
      position: props.tooltipPosition,
      variant: props.tooltipVariant,
      isFullLength: props.tooltipFullLength,
    }
  )
})

const initials = computed(() => getInitials(props.friendlyName || props.name))

const showDescription = computed(() => props.showName && props.description)

const showTextContainer = computed(() => {
  return (
    props.showName && (props.name || props.friendlyName) && !props.useTooltip
  )
})

const isDescriptionTop = computed(() => {
  return showDescription.value && props.descriptionPosition === 'top'
})

const isDescriptionBottom = computed(() => {
  return showDescription.value && props.descriptionPosition === 'bottom'
})

const handleImageLoad = () => {
  isImageLoaded.value = true
}

const handleImageError = () => {
  isImageLoaded.value = false
}
</script>
